import { isAxiosError } from 'axios';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@teachstonellc/component-library';
import RouteHelpers from 'helpers/routes';
import PageLoader from 'components/PageLoader';
import { useNavigate } from 'react-router-dom';
import MainContent from 'components/MainContent';
import useCurrentUser from 'hooks/useCurrentUser';
import { getScores } from 'actions/testPreparations';
import { useRouteParams } from 'hooks/useRouteParams';
import PageBackground from 'components/PageBackground';
import PrintGlobalStyle from 'components/PrintGlobalStyle';
import CalibrationTopSection from 'components/Calibrations/CalibrationTopSection/';
import CalibrationResultSection from 'components/Calibrations/CalibrationResultSection/';
import CalibrationBottomSection from 'components/Calibrations/CalibrationBottomSection/';
import DomainDimensionSection from 'components/Calibrations/ScoreReport/DomainDimensionSection';
import RewatchVideoAccordion from 'components/Calibrations/RewatchVideoAccordion';

type URLParams = {
  id: string;
};

function isForbidden(error: unknown) {
  return isAxiosError(error) && error.response?.status === 403;
}

function ScoreReport() {
  const { id } = useRouteParams<URLParams>();
  const user = useCurrentUser();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['test-preparations', id, 'scores'],
    queryFn: () => getScores(Number(id)).then(res => res.data),
    retry: (failureCount, queryError) =>
      !isForbidden(queryError) && failureCount < 3
  });

  function handleClick() {
    navigate(RouteHelpers.getPath('app'));
  }

  function handleSaveAndExit() {
    navigate(RouteHelpers.getPath('calibrations-view'));
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  }

  if (isLoading) {
    return (
      <div className="mt-8">
        <PageLoader />
      </div>
    );
  }

  if (isForbidden(error)) {
    return (
      <MainContent maxWidth={1280}>
        <div className="mx-auto my-16 flex max-w-xl flex-col items-center gap-4 rounded-lg border border-ds-gray-3 bg-ds-surface-card px-6 py-10 text-center">
          <h1 className="text-2xl font-bold text-[#111827]">
            {i18n.ft(messages.unavailableTitle)}
          </h1>
          <p className="text-[#5E6574]">
            {i18n.ft(messages.unavailableDescription)}
          </p>
          <Button
            variant="primary"
            onClick={() =>
              navigate(RouteHelpers.getPath('calibrations-history'))
            }
          >
            {i18n.ft(messages.backToHistory)}
          </Button>
        </div>
      </MainContent>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <PrintGlobalStyle />
      <MainContent maxWidth={1280}>
        <PageBackground borderStyle className="PrintBackgroundColor">
          <CalibrationTopSection
            title={i18n.ft(messages.pageTitle)}
            userName={user.first_name + ' ' + user.last_name}
            classVersion={
              data.framework === 'class_2008'
                ? 'CLASS 2008'
                : i18n.ft(messages.classSecondEdition)
            }
            ageLevel={data.age_level_group}
            videoTitle={data.video}
            language={data.language}
            dateRange={`${formatDate(data.starts_at)} - ${formatDate(data.ends_at)}`}
            onPrint={window.print}
          />

          <CalibrationResultSection
            title={i18n.ft(messages.calibration)}
            testScore={data.test_score}
            expertCodes={data.expert_code_pdf}
          />

          <div className="PrintBackgroundColor PrintPageBreak bg-[#F9F9F9] mb-0 p-1 sm:px-8 sm:py-3 lg:px-20 lg:py-3 lg:mx-12">
            <DomainDimensionSection
              domains={data.domains}
              messages={messages}
              i18n={i18n}
            />
          </div>

          {data.video_host_id && (
            <RewatchVideoAccordion videoHostId={data.video_host_id} />
          )}

          <CalibrationBottomSection
            onSaveAndExit={handleSaveAndExit}
            onContinue={handleClick}
          />
        </PageBackground>
      </MainContent>
    </>
  );
}

export default ScoreReport;
